'use client'

import { useCallback, useEffect, useMemo, useState } from 'react'
import useSWRImmutable from 'swr/immutable'
import { useSearchParams } from 'next/navigation'
import { type DiscoveryFilter, type MonkeCatalog, readDiscoveryFilters } from '@/utils/nodemonkes'

const SAVED_KEY = 'ow:nodemonkes:saved:v1'
const SAVED_EVENT = 'ow-monkes-saved'
function readSaved(): string[] {
	try {
		const items: unknown = JSON.parse(localStorage.getItem(SAVED_KEY) || '[]')
		return Array.isArray(items) ? items.filter((id): id is string => typeof id === 'string' && /^[a-f0-9]{64}i\d+$/.test(id)) : []
	} catch { return [] }
}
export function useSavedMonkes() {
	const [saved, setSaved] = useState<string[]>([])
	const [error, setError] = useState('')
	useEffect(() => {
		const sync = () => setSaved(readSaved())
		sync()
		window.addEventListener('storage', sync)
		window.addEventListener(SAVED_EVENT, sync)
		return () => { window.removeEventListener('storage', sync); window.removeEventListener(SAVED_EVENT, sync) }
	}, [])
	const toggle = useCallback((id: string) => {
		const current = readSaved()
		const next = current.includes(id) ? current.filter((s) => s !== id) : [...current, id]
		try { localStorage.setItem(SAVED_KEY, JSON.stringify(next)); setError(''); window.dispatchEvent(new Event(SAVED_EVENT)) }
		catch { setError('Favorites could not be saved in this browser.') }
	}, [])
	return { saved, toggle, error }
}

export function useMonkeCatalog(enabled = true) {
	const result = useSWRImmutable<MonkeCatalog>(enabled ? '/data/nodemonkes-catalog.json' : null, async (url: string) => {
		const response = await fetch(url)
		if (!response.ok) throw new Error('Color catalog unavailable')
		return response.json()
	})
	const byId = useMemo(() => new Map(result.data?.items.map((i) => [i.id, i])), [result.data])
	return { ...result, byId }
}

// The existing desktop grid and buy rail share these filters; no new layout.
export function useMonkeFilters(slug: string) {
	const params = useSearchParams()
	const [localFilters, setLocalFilters] = useState<DiscoveryFilter[]>([])
	const enabled = slug === 'nodemonkes'
	const query = params?.get('traits') || '[]'
	const filters = useMemo(() => enabled
		? readDiscoveryFilters(new URLSearchParams({ traits: query })) : localFilters, [enabled, query, localFilters])
	const update = useCallback((value: DiscoveryFilter[] | ((prev: DiscoveryFilter[]) => DiscoveryFilter[])) => {
		if (!enabled) { setLocalFilters(value); return }
		const current = readDiscoveryFilters(new URLSearchParams(window.location.search))
		const next = typeof value === 'function' ? value(current) : value
		const url = new URL(window.location.href)
		if (next.length) url.searchParams.set('traits', JSON.stringify(next)); else url.searchParams.delete('traits')
		// Next's native History API integration updates useSearchParams in both
		// the buy rail and grid without refetching the server-rendered collection.
		window.history.replaceState(null, '', url.pathname + url.search + url.hash)
	}, [enabled])
	return [filters, update] as const
}
